import type { OBJECTS, Patch } from "../../server/servicem8/objects";
import type { Stored } from "../../server/servicem8/store";
import type { TaxRate } from "../../shared/money";
import type { DataProgress } from "../../shared/sync";
import type { GroupTags, NameChange, NameTagConfig, OwnTags } from "./names";
import { type ToolMeta, type ToolPermission, USE_PERMISSION } from "../../shared/tools";

/**
 * What a security role can do in the Price List (Settings › Access), in the order shown. Each is
 * checked on the server for the change it covers (server.ts), and screens follow it (data.can).
 */
export const PRICELIST_PERMISSIONS: readonly ToolPermission[] = [
    { id: USE_PERMISSION, group: "High level", label: "Use the Price List", description: "Open it, and see its items, suppliers, groups and bundles." },
    {
        id: "items",
        group: "What they can change",
        label: "Manage items",
        description: "Add, edit, archive and restore items and bundles; sync them with ServiceM8; review unknown and changed records.",
    },
    {
        id: "pricing",
        group: "What they can change",
        label: "Manage pricing",
        description: "Prices and pricing rules, how items are taxed, costs and where they come from, and groups. Needs See costs.",
        requires: "costs",
    },
    {
        id: "suppliers",
        group: "What they can change",
        label: "Manage suppliers",
        description: "Suppliers and their details, and their prices: adding, updating and rechecking them. Needs See costs.",
        requires: "costs",
    },
    {
        id: "costs",
        group: "What they can see",
        label: "See costs",
        description: "Item costs, supplier prices and their history, pricing rules and groups (a markup gives the cost away), and markups and profit.",
        // Off for a role that could only look: what the business pays and makes isn't for everyone.
        viewOnly: "disabled",
        // Added after the others: roles already managing pricing or suppliers keep seeing what they did.
        startsOnWith: ["pricing", "suppliers"],
    },
    {
        id: "stats",
        group: "What they can see",
        label: "See stats",
        description: "Stats tabs on items, suppliers and groups (use on jobs, sales and profit), and items not used lately. Needs See costs.",
        // Profit is worked out from costs. Off for a role that can only look, as See costs is.
        requires: "costs",
    },
    { id: "settings", group: "What they can change", label: "Change settings", description: "The labour rate, tax on purchases, supplier price checks, minimum markup, name tags and job time." },
];

export const meta: ToolMeta = {
    id: "pricelist",
    name: "Price List",
    description: "Materials and pricing, synced both ways with ServiceM8.",
    icon: "Tags",
    scopes: ["read_inventory"],
    datasets: ["material", "materialbundle", "taxrate", "supplier", "vendor"],
    permissions: PRICELIST_PERMISSIONS,
    sections: [
        { id: "materials", name: "Materials", icon: "Package" },
        { id: "groups", name: "Groups", icon: "Layers" },
        { id: "bundles", name: "Bundles", icon: "Boxes" },
        { id: "suppliers", name: "Suppliers", icon: "Truck" },
        { id: "settings", name: "Settings", icon: "Settings" },
    ],
};


/** What the Price List's routes and screens check (what's being done), and the permission each needs (at least an option of it; default its most). */
export const PRICELIST_ACTIONS = {
    // Items and bundles: Manage items.
    "items.add": ["items"],
    "items.edit": ["items"],
    "items.archive": ["items"],
    "items.sync": ["items"],
    "items.review": ["items"],
    bundles: ["items"],
    // Prices, costs and groups: Manage pricing.
    "items.pricing": ["pricing"],
    "costs.edit": ["pricing"],
    groups: ["pricing"],
    // Suppliers and their prices: Manage suppliers.
    "prices.add": ["suppliers"],
    "prices.update": ["suppliers"],
    "suppliers.edit": ["suppliers"],
    settings: ["settings"],
    // Looking: See costs, See stats.
    "costs.view": ["costs"],
    stats: ["stats"],
} as const satisfies Record<string, readonly [string] | readonly [string, string]>;
export type PriceListAction = keyof typeof PRICELIST_ACTIONS;

/** Actions that only look: they don't need ServiceM8 connected. */
export const LOOKING_ACTIONS: ReadonlySet<PriceListAction> = new Set(["costs.view", "stats"]);

/** Marking a record's changes from ServiceM8 as seen: for whoever manages that kind of record. */
export const REVIEW_ACTIONS = { material: "items.review", supplier: "suppliers.edit", materialbundle: "bundles" } as const satisfies Record<TrackedObject, PriceListAction>;

/** The address keys that open the Price List's windows, over any page (windows.ts). */
export const WINDOW_KINDS = ["item", "supplier", "bundle", "group"] as const;
export type WindowKind = (typeof WINDOW_KINDS)[number];
/** Every address key its windows use: kept as you move between pages, so they stay open. */
export const WINDOW_PARAMS = [...WINDOW_KINDS, "itemTab", "supplierTab", "bundleTab", "groupTab", "rename"] as const;

/*
 * Types shared by the server and the screens. ServiceM8 records (materials, bundles, suppliers)
 * are typed from their descriptions in servicem8/objects.ts.
 */

/**
 * A material, as described in servicem8/objects.ts (ServiceM8's own field names, so an edit goes
 * to ServiceM8 as-is), and where it lives (`synced`, `restoredInServiceM8`). Price and cost are
 * decimal strings in the item's basis (`price_includes_taxes` covers both).
 */
export type MaterialRow = Stored<typeof OBJECTS.material>;

/** Editable ServiceM8 fields of a material. */
export type MaterialPatch = Patch<typeof OBJECTS.material>;

/** A change to a material: its fields, and/or one change to its name's parts (applied to the latest name; see names.ts). */
export type MaterialUpdate = MaterialPatch & { nameChange?: NameChange };

/** manual = a typed base price; percent / fixed = a markup on the cost; parts = a kit's parts' prices added up (a % on top: markupValue). */
export const PRICE_MODES = ["manual", "percent", "fixed", "parts"] as const;
export type PriceMode = (typeof PRICE_MODES)[number];
export const ROUND_MODES = ["none", "up", "down", "nearest"] as const;
export type RoundMode = (typeof ROUND_MODES)[number];

/**
 * How an item's price is worked out (app-owned: only the resulting price goes to ServiceM8).
 *   price = (cost + markup, or the base price) + labour, then rounded.
 * Amounts are stored before tax (see shared/money.ts), so they mean the same whatever the item's
 * basis; screens show and take them in the item's basis.
 */
/** How labour time is shown and typed. */
export const LABOUR_UNITS = ["minutes", "hours"] as const;
export type LabourUnit = (typeof LABOUR_UNITS)[number];

export interface PricingRule {
    mode: PriceMode;
    /** percent: e.g. "35"; fixed: an amount before tax. */
    markupValue: string;
    /** manual: the price before labour, before tax. */
    basePrice: string;
    /** Minutes of labour at the labour rate (0 = none)... */
    labourMinutes: number;
    /** ...shown and typed in minutes or hours (only how it's shown: stored as minutes). */
    labourUnit: LabourUnit;
    roundMode: RoundMode;
    /** Round to steps of this, in the price as the customer sees it (the item's basis), e.g. "5". */
    roundTo: string;
}

export interface ItemPricing extends PricingRule {
    /** The price this app last saved. If ServiceM8's price differs, someone edited it there. */
    appliedPrice: string | null;
    /** Its group, if it's in one: the rule above is then the group's... */
    groupId: string | null;
    /** ...and so is how it's taxed. */
    groupTax: GroupTaxBasis | null;
    /**
     * In a group, but priced its own way: the rule above is its own, and it's taxed as it is (no
     * groupTax). Still in the group for its name tags, lists and stats (pricedByGroup).
     */
    overridesGroup: boolean;
    /** In a group: the name tags it sets itself, with their values, overriding the group's. The rest follow the group. */
    tagOverrides: OwnTags;
    /** In a group that doesn't set pricing (PricingGroupRow setsPricing): false, its rule its own as if it overrode it. Else true or none. */
    groupSetsPricing?: boolean;
    /**
     * A kit (its cost from its parts): its parts' own prices, each times how many, added up, before tax (pricing.ts kitPartsPrice),
     * for pricing it from them ("parts"). Not a kit, or no parts: none.
     */
    partsPrice?: string | null;
}

/** Its price is the group's (in one that sets pricing, not overriding it): the group's pricing changes and updates reach it. */
export const pricedByGroup = (p: Pick<ItemPricing, "groupId" | "overridesGroup" | "groupSetsPricing"> | undefined) => Boolean(p?.groupId && !p.overridesGroup && p.groupSetsPricing !== false);

/** Its rule is its own (not in a group, overriding it, or its group doesn't set pricing): edited on its Pricing tab. */
export const hasOwnRule = <T extends Pick<ItemPricing, "groupId" | "overridesGroup" | "groupSetsPricing">>(p: T | undefined): p is T => Boolean(p && !pricedByGroup(p));

/** In a group, but priced its own way (overriding it, or the group doesn't set pricing): it needs a rule of its own to stay in it. */
export const ownWayInGroup = (p: Pick<ItemPricing, "groupId" | "overridesGroup" | "groupSetsPricing"> | undefined) => Boolean(p?.groupId && !pricedByGroup(p));

/**
 * How an amount is taxed: a ServiceM8 tax rate ("" = the account's default), and whether the amount
 * includes it. How a supplier quotes a price ("$22 inc GST"), how a cost was typed, how a group
 * prices its items. Amounts are stored before tax either way: this is how they're shown and typed.
 */
export interface TaxQuote {
    rateUuid: string;
    includesTax: boolean;
}

/** How a group taxes its items (its rate: the account's default when it has none, or it's gone). */
export type GroupTax = TaxQuote;

/** A group's tax, with its rate as a fraction (0.1 for 10%), for working out prices. */
export interface GroupTaxBasis extends GroupTax {
    tax: number;
}

/** Price List settings for a business. */
export interface PriceListSettings {
    /** Labour rate per hour, as entered (see labourRateIncludesTax). */
    labourRate: string;
    /** Whether the labour rate includes tax (it's taxed at each item's rate). */
    labourRateIncludesTax: boolean;
    /**
     * What an hour of labour costs the business (wages and on-costs); "" = not set. A plain amount:
     * wages carry no tax. Items with labour time have it in their ServiceM8 cost (pricing.ts
     * costWithLabour), so what they make is told on it (itemProfit).
     */
    labourCost: string;
    /**
     * Short names for tax rates (rate uuid -> name), for a long ServiceM8 name ("GST on Income") read
     * shortly ("GST") wherever the Price List shows it (read.ts taxRates). Only here: never sent to
     * ServiceM8, whose names (and the accounting software's link to them) stay as they are.
     */
    taxShortNames: Record<string, string>;
    /**
     * Tax paid on purchases is claimed back (a tax-registered business), so it isn't part of what
     * things cost. Off: it's a real cost. Used for supplier prices.
     */
    purchaseTaxClaimed: boolean;
    /** Items with a markup on their cost (the part's and its labour's) under this % show as making too little (0 = only a loss). */
    minMarkup: number;
    /** Supplier prices not checked for this many days need rechecking (amber), and after `outdatedDays` they're out of date (red). */
    recheckDays: number;
    outdatedDays: number;
    /** Which jobs, and how, work out how long items take (jobTime.ts). */
    jobTime: JobTimeSettings;
}

/**
 * How items' time on jobs is worked out (jobTime.ts): each counted job's time is shared out between the items on it. Only
 * completed jobs count (a work order's time and items may not all be in yet). Each
 * business sets its own: how it records time, and which jobs or items don't follow its usual work.
 */
export interface JobTimeSettings {
    /** Jobs dated (completed, else made a work order) within this many months. */
    lookBackMonths: number;
    /** A job's time includes ServiceM8's estimated drive to it (checking in can start from navigating there). Off: it's taken off. */
    countDrive: boolean;
    /** Jobs taking less than this, or more, are mistakes (a tap in and out, a forgotten check-out): left out. Minutes. */
    minMinutes: number;
    maxMinutes: number;
    /** Customers whose jobs are left out (their work isn't like the rest), with their names as when picked. */
    leaveOutCustomers: { uuid: string; name: string }[];
    /** Items not timed (their quantity isn't work, e.g. a discount): their jobs still count for the other items on them. */
    leaveOutItems: string[];
    /** Jobs with an archived item on are left out (and so archived items aren't timed). */
    leaveOutArchived: boolean;
    /**
     * Counted jobs an item needs to be on before it has a time of its own (fewer: it shares one with the other rare items), and
     * jobs with more than one of it before each extra one does.
     */
    minJobs: number;
    /**
     * Several people on a job: its time counted once (the time anyone was there: people there together count once, one after the
     * other both count), or each person's added up (person-time).
     */
    severalPeople: "once" | "added";
}

export const JOB_TIME_DEFAULTS: JobTimeSettings = {
    lookBackMonths: 24,
    countDrive: true,
    minMinutes: 0,
    maxMinutes: 12 * 60,
    leaveOutCustomers: [],
    leaveOutItems: [],
    leaveOutArchived: true,
    minJobs: 10,
    severalPeople: "once",
};

export interface PriceListData {
    /** For each action (PRICELIST_ACTIONS): null when this person can, else why not (their role, or ServiceM8 not connected). */
    whyNot: Record<PriceListAction, string | null>;
    materials: MaterialRow[];
    taxRates: TaxRate[];
    settings: PriceListSettings;
    /** The business's name tags (names.ts). */
    nameTags: NameTagConfig;
    /** Item names are being updated for a change to the name tags (in the background). */
    namesUpdating: boolean;
    /** Material uuid -> how its price is worked out. Not listed = a plain price, typed in. */
    itemPricing: Record<string, ItemPricing>;
    suppliers: SupplierRow[];
    supplierPrices: SupplierPriceRow[];
    /** Materials whose cost follows a supplier price. */
    costFollows: string[];
    /** Of those, the ones following a price picked for them (material uuid -> price id); the rest follow the cheapest. */
    costPrices: Record<string, string>;
    /** Active items whose cost isn't what the supplier price it follows gives: material uuid -> that cost. "Cost to update" until someone updates it. */
    costUpdates: Record<string, string>;
    /** Costs are being updated now ("Update all": in the background). */
    costsUpdating: boolean;
    /** How an item's typed cost was last typed (material uuid -> quote); others start ex its own rate. */
    costQuotes: Record<string, TaxQuote>;
    /** An item's typed cost exactly as last typed (in its costQuotes quote), shown back while ServiceM8's cost still matches it. */
    costTyped: Record<string, string>;
    /** The labour in an item's ServiceM8 cost, before tax (material uuid -> amount; not listed: none). Its part costs the rest (pricing.ts partItem). */
    labourInCost: Record<string, string>;
    /** The labour time that was for (material uuid -> minutes; not listed: not recorded): what its ServiceM8 figures were set with. */
    labourInMinutes: Record<string, number>;
    /** Why suppliers can't sync with ServiceM8 right now (they still work, kept in this app), or null. */
    supplierSyncUnavailable: string | null;
    pricingGroups: PricingGroupRow[];
    bundles: BundleRow[];
    /** Why bundles can't sync with ServiceM8 right now (they still work, kept in this app), or null. */
    bundleSyncUnavailable: string | null;
    /** Items, suppliers and bundles changed in ServiceM8 that no one has seen yet (history.ts): each a warning until seen. */
    changedInServiceM8: ChangedRecords;
    /** Active items no one has said are real yet (known.ts): each a warning until marked known. */
    unknownItems: string[];
    /** Kits (items whose cost comes from their parts: kits.ts) -> their parts, in order (none yet: []). */
    kitParts: Record<string, KitPart[]>;
    /** Items whose price is locked by themselves (locks.ts); their group can lock them too (PricingGroupRow.locksPrices). */
    lockedItems: string[];
    /** Locked items whose price or tax was changed in ServiceM8 since (locks.ts), not seen yet: each needs attention. */
    lockedPriceChanged: string[];
    /** When each item was last on a job (stats.ts: "YYYY-MM-DD"; not there: never), or null until jobs and job lines are read in full. */
    lastUsed: Record<string, string> | null;
}

/** Records some change was to, by kind (rows.ts, changed.ts): items, suppliers, bundles. */
export type RecordScope = Partial<Record<"material" | "supplier" | "materialbundle", string[]>>;

/**
 * The Price List's data for some records only (rows.ts): what replaces theirs in PriceListData.
 * Each kind lists the uuids asked for (`uuids`), and the rows of those still here (the rest are gone).
 */
export interface PriceListRows {
    material?: {
        uuids: string[];
        rows: MaterialRow[];
        itemPricing: Record<string, ItemPricing>;
        supplierPrices: SupplierPriceRow[];
        costFollows: string[];
        costPrices: Record<string, string>;
        costUpdates: Record<string, string>;
        costQuotes: Record<string, TaxQuote>;
        costTyped: Record<string, string>;
        labourInCost: Record<string, string>;
        labourInMinutes: Record<string, number>;
        unknownItems: string[];
        changed: string[];
        lockedItems: string[];
        lockedPriceChanged: string[];
        kitParts: Record<string, KitPart[]>;
        /** Null: not allowed, or jobs not read in full yet (as PriceListData's). */
        lastUsed: Record<string, string> | null;
    };
    supplier?: { uuids: string[]; rows: SupplierRow[]; changed: string[] };
    materialbundle?: { uuids: string[]; rows: BundleRow[]; changed: string[] };
}

/** ServiceM8: item numbers are at most 30 characters, unique across materials and bundles. */
export const ITEM_NUMBER_MAX = 30;
/** A money amount as typed: digits, optionally negative, up to 4 decimals. */
export const MONEY_PATTERN = /^-?\d+(\.\d{1,4})?$/;
/** A markup as stored: up to 8 decimals (a % switched from an amount is kept exactly: pricing.ts percentFor). */
export const MARKUP_PATTERN = /^\d+(\.\d{1,8})?$/;

// ---------------------------------------------------------------------------------------------
// Suppliers
// ---------------------------------------------------------------------------------------------

/** Ways a supplier can be paid. */
export const PAYMENT_METHODS = [
    { id: "account", label: "On account" },
    { id: "card", label: "Card" },
    { id: "transfer", label: "Bank transfer" },
    { id: "cash", label: "Cash" },
] as const;

/** Ways to order from a supplier, each with a detail to fill in (where to go, or send it). */
export const ORDER_METHODS = [
    { id: "online", label: "Online store", detail: "Website", placeholder: "https://", kind: "url" },
    { id: "po", label: "Purchase order", detail: "Email it to", placeholder: "orders@supplier.com", kind: "email" },
    { id: "quote", label: "Email for a quote", detail: "Email", placeholder: "sales@supplier.com", kind: "email" },
    { id: "phone", label: "Phone", detail: "Number", placeholder: "Phone number", kind: "phone" },
    { id: "counter", label: "In store / trade counter", detail: "Where", placeholder: "Branch or address", kind: "text" },
    { id: "rep", label: "Through our rep", detail: null, placeholder: "", kind: "text" },
] as const;

/** The app's own details for a supplier (ServiceM8 has nowhere for them). */
export interface SupplierDetails {
    /** How their prices are usually quoted (each price starts this way): with tax or without, at which rate ("" = the default rate). */
    pricesIncludeTax: boolean;
    purchaseTaxRateUuid: string;
    hasAccount: boolean;
    accountTerms: string;
    repName: string;
    repPhone: string;
    repEmail: string;
    /** PAYMENT_METHODS ids they take, and the one we prefer (one of them, or null). */
    paymentMethods: string[];
    preferredPayment: string | null;
    /** ORDER_METHODS ids they take, with their detail, and the one we prefer. */
    orderMethods: { method: string; detail: string }[];
    preferredOrder: string | null;
    notes: string;
}

export const NO_DETAILS: SupplierDetails = {
    pricesIncludeTax: false,
    purchaseTaxRateUuid: "",
    hasAccount: false,
    accountTerms: "",
    repName: "",
    repPhone: "",
    repEmail: "",
    paymentMethods: [],
    preferredPayment: null,
    orderMethods: [],
    preferredOrder: null,
    notes: "",
};

/** A supplier: ServiceM8's fields (servicem8/objects.ts), where it lives, and the app's details. */
export type SupplierRow = Stored<typeof OBJECTS.supplier> & { details: SupplierDetails };

/** Editable ServiceM8 fields of a supplier. */
export type SupplierPatch = Patch<typeof OBJECTS.supplier>;

/** Where an item's cost comes from: typed in, its cheapest supplier price, or one price picked for it (bulk or not). */
export type CostSource = { from: "typed" } | { from: "cheapest" } | { from: "price"; priceId: string } | { from: "parts" };

/** One of a kit's parts: another item, so many of it (kits.ts). */
export interface KitPart {
    partUuid: string;
    quantity: number;
}

/** What a supplier charges for one material, from a minimum quantity. Stored before tax. */
export interface SupplierPriceRow {
    id: string;
    materialUuid: string;
    supplierUuid: string;
    supplierCode: string;
    /** Link to the item on the supplier's website ("" = none). */
    url: string;
    /** The least you have to order for this price (1 = no minimum). */
    minQty: number;
    /** Price for one, before tax (decimal string). */
    priceNet: string;
    /** How it's quoted (its rate is the tax paid on it), or null: as its supplier usually quotes (supplierPricing.ts quoteOf). */
    quote: TaxQuote | null;
    /** Epoch ms: last confirmed (changed or not), and last actually changed. */
    checkedAt: number;
    changedAt: number;
}

export interface PriceHistoryEntry {
    id: string;
    priceNet: string;
    minQty: number;
    changedAt: number;
    /** Who made the change (name or email), if known. */
    changedBy: string | null;
    /** Marked as a mistake: left out of the trend. */
    ignored: boolean;
}

/** Re-editing your own price within this window corrects the last history entry instead of adding one. */
export const HISTORY_CORRECTION_MS = 15 * 60_000;

// ---------------------------------------------------------------------------------------------
// Groups, bundles
// ---------------------------------------------------------------------------------------------

/** Pricing shared by several items. Its rule's amounts are before tax, like an item's own. */
export interface PricingGroupRow {
    id: string;
    name: string;
    /** What it's for (its Details tab): only kept here. */
    description: string;
    /** Its amounts are stored before tax, and shown and typed in its tax basis. */
    rule: PricingRule;
    /** How its items are taxed: part of its pricing (an item taxed otherwise doesn't match it). */
    tax: GroupTax;
    /** The name tags it sets for its items (the rest are up to each item). */
    tags: GroupTags;
    /** Every item in it has its price (and tax) locked (locks.ts). */
    locksPrices: boolean;
    /** It sets its items' pricing (rule, tax) and name tags (tags): either can be off, or both (just a group of items). */
    setsPricing: boolean;
    setsTags: boolean;
}

export type { BundleLine } from "../../server/servicem8/objects";

/** A ServiceM8 material bundle (servicem8/objects.ts): several items sold together. And where it lives. */
export type BundleRow = Stored<typeof OBJECTS.materialbundle>;

/** ServiceM8: a bundle holds at most this many materials. */
export const BUNDLE_MAX_ITEMS = 50;

// ---------------------------------------------------------------------------------------------
// Stats (stats.ts): from the items on jobs
// ---------------------------------------------------------------------------------------------

/** The first day of the month 11 months ago ("2025-11-01"): "the last 12 months" is this month and the 11 before. */
export function yearStart(now = new Date()) {
    const d = new Date(now.getFullYear(), now.getMonth() - 11, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-01`;
}

/**
 * An item's active jobs: quotes, and work orders and completed jobs not yet approved (in ServiceM8 a
 * quote is a job too). The same everywhere it's shown (its Stats tab, its item number's hint).
 */
export const activeJobs = (s: Pick<ItemStats, "openJobs" | "openQuotes">) => s.openJobs + s.openQuotes;

/** "2 active jobs". */
export const activeJobsText = (n: number) => `${n.toLocaleString()} active ${n === 1 ? "job" : "jobs"}`;

/** Jobs and job lines, which stats are worked out from: each turned on, allowed and read in full? */
export interface JobData {
    jobs: DataProgress;
    lines: DataProgress;
    /** Job time (job activities): how long items take. */
    times: DataProgress;
}

/** Use on jobs (work orders and completed) over a period: how many jobs, how many of it, and sales and cost before tax. */
export interface Usage {
    jobs: number;
    quantity: number;
    sales: number;
    cost: number;
    /** Of those jobs, how many charged for it with no cost recorded (it had none then): their profit counts in full, so profit may be high. */
    noCostJobs: number;
}

/** Use in one bar of a period's chart: from `from` ("YYYY-MM-DD") to the next bar (shared/periods.ts barStarts). */
export interface BarUsage {
    from: string;
    quantity: number;
    sales: number;
}

export interface ItemStats {
    /** Use in the period asked for (the last 12 months unless another is chosen), and through it as bars. */
    period: Usage;
    bars: BarUsage[];
    /** Quotes in the period: how many it was on, and how many of it. */
    quotes: { quotes: number; quantity: number };
    allTime: Usage;
    /** The last job it was on ("YYYY-MM-DD"), or null. */
    lastUsed: string | null;
    /** Jobs not yet approved that it's on (work orders and completed). With openQuotes, its "active jobs" (activeJobs). */
    openJobs: number;
    /** Quotes it's on that are still quotes (not accepted or unsuccessful yet), of any age. */
    openQuotes: number;
    /** Its active jobs (activeJobs), newest first: the first ACTIVE_JOBS_LISTED. */
    activeList: ActiveJob[];
    /** How long it takes on jobs (stats.ts itemTime); null without job time. */
    time: ItemTime | null;
}

/**
 * How long an item takes on a job (jobTime.ts), in minutes: the first of it, and each extra one on the same job. Null: too few
 * jobs to tell (under the minJobs setting, or that many with more than one).
 */
export interface ItemJobTime {
    uuid: string;
    /** Counted jobs it's on, and how many of them have more than one of it. */
    jobs: number;
    multiJobs: number;
    first: number | null;
    extra: number | null;
}

/** An item's Stats tab: its time (as the Price List settings' preview has it), and how it was worked out. */
export interface ItemTime extends ItemJobTime {
    /** Left out in Price List settings (Leave out items): not timed. */
    leftOut: boolean;
    countDrive: boolean;
    lookBackMonths: number;
    minJobs: number;
    /** The time every job takes before its items (worked out or set), that its times are on top of. */
    everyJob: number | null;
}

/**
 * How long a group's items take (jobTime.ts), in minutes: its timed items' times together, each counted by how often it's used
 * (first: by its jobs; extra: by its jobs with more than one), and the fastest and slowest of their first ones. Null: none timed.
 */
export interface GroupJobTime {
    id: string;
    /** Its items, and those with a time of their own (on enough jobs, not left out). */
    items: number;
    timed: number;
    /** Counted jobs with any of its items on. */
    jobs: number;
    first: number | null;
    extra: number | null;
    fastest: number | null;
    slowest: number | null;
}

/** A group's Stats tab: its time, and how it was worked out. */
export interface GroupTime extends GroupJobTime {
    countDrive: boolean;
    lookBackMonths: number;
    minJobs: number;
    everyJob: number | null;
}

/** What the settings give (Price List settings' preview), to see at a glance whether they're believable. */
export interface JobTimePreview {
    data: JobData;
    /** Jobs counted, and of those found, how many were left out with their customers. */
    jobs: number;
    customerJobs: number;
    /** ...and of the rest, how many were left out for an archived item on them. */
    archivedJobs: number;
    /** The base time per visit: what every visit to a job takes before its items (null: no jobs to tell). */
    everyJob: number | null;
    /** A visit's usual drive there (ServiceM8's estimate), in its time or taken off it (countDrive). */
    drive: number | null;
    /** A counted job's usual time, and how far a job's time usually is from what its items add up to (median, minutes). */
    typicalJob: number | null;
    miss: number | null;
    /** Every item on a counted job (those left out too, without times), most jobs first. */
    items: ItemJobTime[];
    /** Every group with an item on a counted job, most jobs first. */
    groups: GroupJobTime[];
    /** What each rare item adds (one time for all of them). */
    rare: number | null;
    /** Customers with the most counted jobs, for leaving out (names need Clients synced: `clients`). */
    customers: { uuid: string; name: string; jobs: number }[];
    clients: DataProgress;
}

/** Active jobs listed on an item's Stats tab, at most (the count says how many in all). */
export const ACTIVE_JOBS_LISTED = 100;

/** One of an item's active jobs: a quote, or a work order or completed job not yet approved. */
export interface ActiveJob {
    uuid: string;
    /** The job number people know it by ("1042"), or "" if it has none. */
    number: string;
    /** As in ServiceM8: Quote, Work Order or Completed. */
    status: string;
    /** Quoted, completed, made a work order or booked ("YYYY-MM-DD"), or "". */
    day: string;
    /** How many of the item are on it, and their amount before tax, as on the job. */
    quantity: number;
    sales: number;
}

export interface SupplierStats {
    /** Items with a price from them, and for how many they're the cheapest, or what the cost follows. */
    items: number;
    cheapestFor: number;
    costFollows: number;
    /** Their prices due for a recheck, and when one was last checked (ms), or null. */
    due: number;
    lastChecked: number | null;
    /** Their price changes in the period asked for: how many, the average, and the latest increase. */
    changes: { count: number; averagePct: number | null; lastIncrease: { at: number; pct: number; item: string } | null };
    /** Cost of the items used on jobs in the period whose cost comes from them: what's likely bought from them. Null without job data. */
    estimatedSpend: number | null;
}

export interface GroupStats {
    items: number;
    /** Its items' use in the period asked for. */
    period: Usage;
    /** Markup achieved on those jobs (sales over cost, before tax), or null without cost. */
    markupPct: number | null;
    /** Its most used items in the period, by sales. */
    top: { uuid: string; name: string; quantity: number; sales: number }[];
    /** How long its items take on jobs (jobTime.ts); null without job time. */
    time: GroupTime | null;
}

export interface StatsResponse<T> {
    data: JobData;
    stats: T;
}

// ---------------------------------------------------------------------------------------------
// History (history.ts): what changed on items, suppliers and bundles, here or in ServiceM8
// ---------------------------------------------------------------------------------------------

/** The kinds of record with a history (ServiceM8 object names). */
export const TRACKED_OBJECTS = ["material", "supplier", "materialbundle"] as const;
export type TrackedObject = (typeof TRACKED_OBJECTS)[number];

/** One change to one field: its values before and after (as the app reads them), when, and where (here: by whom, if known). */
export interface ChangeRow {
    id: string;
    /** A field, or "created" (made here). */
    field: string;
    before: unknown;
    after: unknown;
    at: number;
    source: "app" | "servicem8";
    by: string | null;
    /** Seen: made here, history only (e.g. stock), or someone pressed Seen. */
    seen: boolean;
}

/** Records with changes from ServiceM8 no one has seen yet, by kind. */
export type ChangedRecords = Record<TrackedObject, string[]>;

/** What an item is found by when searched, here and on the home page: its name, item number, barcode and group's name. */
export const itemSearchText = (m: Pick<MaterialRow, "name" | "item_number" | "barcode">, groupName: string | undefined) => `${m.name} ${m.item_number} ${m.barcode} ${groupName ?? ""}`;

/** Tax rates' short names with these changed (blank: none, its full name again), the rest as they are. The server saves them so, and the screen shows them so while it does. */
export function withShortNames(current: Record<string, string>, changes: Record<string, string>) {
    const next = { ...current, ...changes };
    for (const [uuid, name] of Object.entries(changes)) if (!name) delete next[uuid];
    return next;
}

/**
 * Another rate already read as `name` (its short name, else its full one), which would make the two
 * impossible to tell apart wherever they're picked: that rate's full name, or null.
 */
export function shortNameTaken(rates: readonly { uuid: string; name: string; fullName?: string }[], names: Record<string, string>, uuid: string, name: string): string | null {
    const wanted = name.trim().toLowerCase();
    if (!wanted) return null;
    const other = rates.find((r) => r.uuid !== uuid && (names[r.uuid] || (r.fullName ?? r.name)).trim().toLowerCase() === wanted);
    return other ? (other.fullName ?? other.name) : null;
}

/**
 * Price locks (locks.ts): an item's price and tax can be locked (e.g. it's on the business's website),
 * by itself or by its group (every item in it). Locked, a change here needs a confirmation
 * that says so, and a change in ServiceM8 needs attention.
 */
export type PriceLock = { by: "item" } | { by: "group"; group: string };

/** What locks an item's price, from what's kept about it: itself (first), its group, or nothing. The one rule, here and on the server (locks.ts). */
export function resolveLock(lockedItself: boolean, group: { name: string; locksPrices: boolean } | undefined): PriceLock | null {
    if (lockedItself) return { by: "item" };
    return group?.locksPrices ? { by: "group", group: group.name } : null;
}

/** An item's price fields: what a lock covers (a change to any is a price change). */
export const PRICE_FIELDS = ["price", "price_includes_taxes", "tax_rate_uuid"] as const;

type LockData = Pick<PriceListData, "lockedItems" | "itemPricing" | "pricingGroups">;
// Lookups for the locks, once per data load (each row asks): items locked themselves, and groups by id.
const lockLookups = new WeakMap<LockData, { locked: ReadonlySet<string>; groups: ReadonlyMap<string, PricingGroupRow> }>();

/** What locks an item's price: itself (first), its group, or nothing (null). */
export function priceLock(data: LockData, uuid: string): PriceLock | null {
    let look = lockLookups.get(data);
    if (!look) {
        look = { locked: new Set(data.lockedItems), groups: new Map(data.pricingGroups.map((g) => [g.id, g])) };
        lockLookups.set(data, look);
    }
    const groupId = data.itemPricing[uuid]?.groupId;
    return resolveLock(look.locked.has(uuid), groupId ? look.groups.get(groupId) : undefined);
}

/** What a lock says ("Price locked", "Price locked by the Deadbolts group"). */
export const lockText = (lock: PriceLock) => (lock.by === "item" ? "Price locked" : `Price locked by the ${lock.group} group`);
